import { useState, useEffect, useCallback, useMemo } from "react";

/**
 * 管理 effectType 選取狀態的 Hook
 * 負責處理變更單中 effectType 的選取、檢查、查詢等邏輯
 *
 * @param {Object} options - 設定選項
 * @param {Array<number>} [options.initialSelected=[]] - 初始已選取的 effectType（用於編輯模式）
 * @returns {Object} effectType 選取相關的狀態和方法
 */
const useEffectTypeSelection = ({ initialSelected = [] } = {}) => {
	const [selectedEffectTypes, setSelectedEffectTypes] = useState(() => new Set(initialSelected));

	// 用於監聽 initialSelected 變更的 key
	const initialSelectedKey = useMemo(
		() => {
			if (!initialSelected || !initialSelected.sort) {
				return "[]";
			}
			return JSON.stringify([...initialSelected].sort());
		},
		// eslint-disable-next-line
		[initialSelected],
	);

	// 當 initialSelected 變更時，同步更新狀態（用於編輯模式）
	useEffect(
		() => {
			setSelectedEffectTypes(new Set(initialSelected));
		},
		// eslint-disable-next-line
		[initialSelectedKey],
	);

	/**
	 * 處理 effectType 選取變更
	 * @param {number} effectTypeID - effectType ID
	 * @param {boolean} isSelected - 是否選取
	 */
	const handleEffectTypeChange = useCallback(
		(effectTypeID, isSelected) => {
			setSelectedEffectTypes(prev => {
				const newSet = new Set(prev);
				if (isSelected) {
					newSet.add(effectTypeID);
				} else {
					newSet.delete(effectTypeID);
				}
				return newSet;
			});
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 檢查 effectType 是否已選取
	 * @param {number} effectTypeID - effectType ID
	 */
	const isEffectTypeSelected = useCallback(
		effectTypeID => {
			return selectedEffectTypes.has(effectTypeID);
		},
		// eslint-disable-next-line
		[selectedEffectTypes],
	);

	/**
	 * 取得所有選取的 effectType 陣列（用於監聽變化）
	 * @return {Array<number>} 選取的 effectType ID 陣列
	 */
	const selectedEffectTypesArray = useMemo(
		() => {
			return Array.from(selectedEffectTypes).sort((a, b) => a - b);
		},
		// eslint-disable-next-line
		[selectedEffectTypes],
	);

	/**
	 * 取得所有選取的 effectType 陣列（不排序）
	 * @return {Array<number>} 選取的 effectType ID 陣列
	 */
	const getSelectedEffectTypesArray = useCallback(
		() => {
			return Array.from(selectedEffectTypes);
		},
		// eslint-disable-next-line
		[selectedEffectTypes],
	);

	/**
	 * 檢查是否至少選取一個
	 * @return {boolean} 是否有選取任何 effectType
	 */
	const hasAnySelected = useMemo(
		() => {
			return selectedEffectTypes.size > 0;
		},
		// eslint-disable-next-line
		[selectedEffectTypes],
	);

	/**
	 * 清空所有選取
	 * @return {void}
	 */
	const clearAll = useCallback(
		() => {
			setSelectedEffectTypes(new Set());
		},
		// eslint-disable-next-line
		[],
	);

	return {
		selectedEffectTypes,
		selectedEffectTypesArray, // 用於監聽 effectType 選取的變化
		hasAnySelected,
		handleEffectTypeChange,
		isEffectTypeSelected,
		getSelectedEffectTypesArray,
		clearAll,
	};
};

export default useEffectTypeSelection;
